import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSpinner, FaPlay, FaCheck } from 'react-icons/fa';
import api from '../utils/api';
import Certificate from './Certificate';
import { useAuth } from '../context/AuthContext';
import 'react-toastify/dist/ReactToastify.css';

const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    // Regex to extract YouTube video ID from various URL formats
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|embed\/|v\/|)([\w-]{11})(&.*)?/;
    const match = url.match(regex);
    if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}`;
    }
    return null; 
};

const CourseContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [completedLessons, setCompletedLessons] = useState([]);
  const { user, markCourseCompleted } = useAuth();
  const [certificateIssued, setCertificateIssued] = useState(false);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await api.get(`/courses/${id}`);
        setCourse(response.data);
        // Default to first lesson
        if (response.data.lessons && response.data.lessons.length > 0) {
          setSelectedLesson(response.data.lessons[0]);
        }
        setLoading(false);
      } catch (err) {
        setError('Failed to load course content. Please try again later.');
        setLoading(false);
      }
    };
    fetchCourse();
  }, [id]);

  const handleLessonClick = (lesson) => {
    setSelectedLesson(lesson);
    if (!completedLessons.includes(lesson._id)) {
      setCompletedLessons([...completedLessons, lesson._id]);
    }
  };

  const courseCompleted = course && course.lessons && completedLessons.length === course.lessons.length;

  useEffect(() => {
    if (courseCompleted && !certificateIssued) {
      markCourseCompleted(id);
      setCertificateIssued(true);
    }
    // eslint-disable-next-line
  }, [courseCompleted, certificateIssued, markCourseCompleted, id]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <FaSpinner className="fa-spin fa-3x text-primary mb-3" />
        <h4>Loading course content...</h4>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5 text-center">
        <h4 className="text-danger">{error}</h4>
        <button 
          className="btn btn-primary mt-3"
          onClick={() => navigate('/courses')}
        >
          Back to Courses
        </button>
      </div>
    );
  }

  return (
    <div className="container py-5" style={{ marginTop: '75px' }}>
      <div className="row g-4 flex-column-reverse flex-lg-row">
        {/* Lessons List */}
        <div className="col-12 col-lg-4">
          <div className="card shadow-sm mb-4 h-100 overflow-auto" style={{ maxHeight: '500px' }}>
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Lessons</h5>
            </div>
            <ul className="list-group list-group-flush">
              {course.lessons && course.lessons.length > 0 ? (
                course.lessons.map((lesson, idx) => (
                  <li
                    key={lesson._id || idx}
                    className={`list-group-item d-flex align-items-center justify-content-between ${selectedLesson && selectedLesson._id === lesson._id ? 'active text-white' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleLessonClick(lesson)}
                    tabIndex={0}
                    aria-label={`Lesson: ${lesson.title}`}
                  >
                    <span className="text-truncate">
                      {completedLessons.includes(lesson._id) ? (
                        <FaCheck className="text-success me-2" />
                      ) : (
                        <FaPlay className="text-primary me-2" />
                      )}
                      {lesson.title}
                    </span>
                    <span className="badge bg-secondary rounded-pill ms-2">
                      {lesson.duration} min
                    </span>
                  </li>
                ))
              ) : (
                <li className="list-group-item text-muted">No lessons available.</li>
              )}
            </ul>
          </div>
        </div>
        {/* Video Player and Lesson Details */}
        <div className="col-12 col-lg-8">
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h3 className="fw-bold mb-2 text-truncate">{selectedLesson ? selectedLesson.title : course?.title}</h3>
              {course && course.instructor && (
                <div className="mb-2 text-muted small text-truncate">
                  Instructor: {course.instructor.name} ({course.instructor.email})
                </div>
              )}
              {selectedLesson ? (
                <>
                  <div className="ratio ratio-16x9 mb-4 rounded-3 overflow-hidden">
                    <iframe
                      src={getYouTubeEmbedUrl(selectedLesson.videoUrl)}
                      title={selectedLesson.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ border: 0 }}
                    />
                  </div>
                  <p className="text-muted mb-2 text-wrap">{selectedLesson.description}</p>
                  <div className="mb-2">
                    <span className="badge bg-primary me-2">Duration: {selectedLesson.duration} min</span>
                    <span className="badge bg-info">Order: {selectedLesson.order}</span>
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  <h5>Select a lesson to start learning!</h5>
                </div>
              )}
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Course Progress</h5>
              <div className="progress mb-3" style={{ height: '1.5rem' }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{
                    width: `${(completedLessons.length / (course.lessons?.length || 1)) * 100}%`
                  }}
                  aria-valuenow={Math.round((completedLessons.length / (course.lessons?.length || 1)) * 100)}
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {Math.round((completedLessons.length / (course.lessons?.length || 1)) * 100)}%
                </div>
              </div>
              <p className="card-text">
                {completedLessons.length} of {course.lessons?.length || 0} lessons completed
              </p>
              {courseCompleted && user && (
                <div className="mt-4">
                  <Certificate
                    student={user}
                    course={course}
                    issueDate={new Date().toLocaleDateString()}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseContent; 